import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Package } from 'lucide-react';

export const OptionalComponentsPanel = ({ components, onChange }) => {
  const [newComponent, setNewComponent] = useState({
    name: '',
    quantity: 1,
    unit_cost: 0,
    include_gst: false
  });

  const handleAdd = () => {
    if (!newComponent.name || newComponent.unit_cost <= 0) return;

    onChange([...components, { ...newComponent }]);
    setNewComponent({
      name: '',
      quantity: 1,
      unit_cost: 0,
      include_gst: false
    });
  };

  const handleRemove = (index) => {
    onChange(components.filter((_, i) => i !== index));
  };

  const handleUpdate = (index, field, value) => {
    const updated = [...components];
    updated[index][field] = value;
    onChange(updated);
  };

  const totalCost = components.reduce((sum, comp) => {
    const cost = comp.quantity * comp.unit_cost;
    return sum + (comp.include_gst ? cost * 1.1 : cost);
  }, 0);

  return (
    <Card className="p-5 bg-white border-gray-200 shadow-md hover:shadow-lg transition-all">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-pink-100">
            <Package className="h-4 w-4 text-pink-600" />
          </div>
          <h3 className="font-semibold text-gray-800">Optional Components</h3>
        </div>

        {/* Existing components */}
        {components.length > 0 && (
          <div className="space-y-2">
            {components.map((comp, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200"
              >
                <div className="flex-1 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-gray-600 mb-1">Name</div>
                    <Input
                      value={comp.name}
                      onChange={(e) => handleUpdate(index, 'name', e.target.value)}
                      className="bg-white border-gray-300 h-8 text-xs"
                    />
                  </div>
                  <div>
                    <div className="text-gray-600 mb-1">Qty</div>
                    <Input
                      type="number"
                      value={comp.quantity}
                      onChange={(e) => handleUpdate(index, 'quantity', parseFloat(e.target.value))}
                      className="bg-white border-gray-300 h-8 text-xs"
                    />
                  </div>
                  <div>
                    <div className="text-gray-600 mb-1">Unit Cost</div>
                    <Input
                      type="number"
                      value={comp.unit_cost}
                      onChange={(e) => handleUpdate(index, 'unit_cost', parseFloat(e.target.value))}
                      className="bg-white border-gray-300 h-8 text-xs"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1 items-center">
                  <div className="flex items-center gap-1">
                    <Checkbox
                      checked={comp.include_gst}
                      onCheckedChange={(checked) => handleUpdate(index, 'include_gst', checked)}
                      className="border-gray-400"
                    />
                    <span className="text-xs text-gray-600">GST</span>
                  </div>
                  <Button
                    data-testid={`remove-component-${index}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemove(index)}
                    className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
            <div className="text-right text-sm font-semibold text-gray-800 pt-2 border-t border-gray-200">
              Subtotal: ${totalCost.toFixed(2)}
            </div>
          </div>
        )}

        {/* Add new component */}
        <div className="space-y-2 pt-2 border-t border-gray-200">
          <Label className="text-xs text-gray-700">Add Component</Label>
          <div className="grid grid-cols-3 gap-2">
            <Input
              data-testid="component-name-input"
              placeholder="Name"
              value={newComponent.name}
              onChange={(e) => setNewComponent({ ...newComponent, name: e.target.value })}
              className="bg-gray-50 border-gray-300 text-sm"
            />
            <Input
              data-testid="component-quantity-input"
              type="number"
              placeholder="Qty"
              value={newComponent.quantity}
              onChange={(e) => setNewComponent({ ...newComponent, quantity: parseFloat(e.target.value) })}
              className="bg-gray-50 border-gray-300 text-sm"
            />
            <Input
              data-testid="component-cost-input"
              type="number"
              placeholder="Unit Cost"
              value={newComponent.unit_cost}
              onChange={(e) => setNewComponent({ ...newComponent, unit_cost: parseFloat(e.target.value) })}
              className="bg-gray-50 border-gray-300 text-sm"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                data-testid="component-gst-checkbox"
                checked={newComponent.include_gst}
                onCheckedChange={(checked) => setNewComponent({ ...newComponent, include_gst: checked })}
                className="border-gray-400"
              />
              <Label className="text-xs text-gray-700">Include GST (10%)</Label>
            </div>
            <Button
              data-testid="add-component-btn"
              size="sm"
              onClick={handleAdd}
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};